import cx from 'classnames'
import { useCallback, useMemo } from 'react'
import { isNil, uniqBy } from 'lodash'
import { useFeature } from '@growthbook/growthbook-react'

import { Post } from 'lib/_types'
import { ROUTE_PATH } from 'lib/paths'
import { abbreviateNumber } from 'lib/helpers'
import { useCurrentUser } from 'store/api/yay/user'

import Heart from './Heart'

import './style.scss'

type CustomPost = Post & {
  _likedRecently?: boolean
  _isLoading?: boolean
}

type PostActionsProps = {
  onReply?(post: CustomPost): void
  onRepost?(post: CustomPost): void
  onToggleLike?(post: CustomPost): void
  post?: CustomPost
  isMuted?: boolean
}

const PostActions = ({
  onReply,
  onRepost,
  onToggleLike,
  post,
  isMuted,
}: PostActionsProps) => {
  const hideCountIfEmpty = useCallback(
    (count) =>
      count > 0 && typeof count !== 'undefined'
        ? abbreviateNumber(count)
        : undefined,
    []
  )

  const repostCount = useMemo(
    () => hideCountIfEmpty(post?.reposts_count),
    [post]
  )

  const likeCount = useMemo(() => hideCountIfEmpty(post?.likes_count), [post])

  const giftCount = hideCountIfEmpty(post?.total_gifts_count)

  const replyCount = useMemo(
    () => hideCountIfEmpty(post?.in_reply_to_post_count),
    [post]
  )

  const { data: currentUser } = useCurrentUser()

  const isGroupPost = !isNil(post?.group_id)

  const isEmplGiftsEnabled = useFeature('yay-17610')?.on

  // https://jira.nanameue.jp/browse/YAY-6762
  const repostAble =
    (post?.repostable && (post?.shareable?.post?.repostable ?? true)) ||
    currentUser?.id === post?.user?.id ||
    post?.shareable?.post?.repostable

  return (
    <ul className="PostActions">
      {onReply && (
        <li>
          <a
            className={cx('PostActions__a PostActions__a--reply', {
              'PostActions__a--x-disabled': post?._isLoading,
            })}
            onClick={() => onReply(post)}
          />
          <span>{replyCount}</span>
        </li>
      )}

      {onRepost && repostAble && !isMuted && (
        <li>
          <a
            className={cx('PostActions__a PostActions__a--reposts', {
              'PostActions__a--reposts--active': post?.reposted,
              'PostActions__a--x-disabled': post?._isLoading,
            })}
            onClick={() => onRepost(post)}
          />
          <span>{repostCount}</span>
        </li>
      )}

      {onToggleLike && (
        <li>
          <a
            className={cx('PostActions__a PostActions__a--likes', {
              'PostActions__a--likes--active': post?.liked,
              'PostActions__a--x-disabled': post?._isLoading,
            })}
            onClick={() => onToggleLike(post)}
          >
            <Heart active={post?._likedRecently} liked={post?.liked} />
          </a>
          <span>{likeCount}</span>
        </li>
      )}

      {isEmplGiftsEnabled && (
        <li>
          <a
            className={cx('PostActions__a PostActions__a--gifts', {
              'PostActions__a--gifts--active': giftCount > 0,
              'PostActions__a--x-disabled': post?._isLoading,
            })}
            href={
              isGroupPost
                ? `${ROUTE_PATH.GROUP_POST_GIFTS(
                    post?.group_id,
                    post?.id
                  )}?sendGift=true`
                : `${ROUTE_PATH.POST_GIFTS(post?.id)}?sendGift=true`
            }
          />
          <span>{giftCount}</span>
        </li>
      )}
    </ul>
  )
}

export default PostActions
